/**
 * PDF Generation API Endpoints
 *
 * Client functions for PDF generation API calls.
 * These functions are used by React Query hooks.
 */

import { supabase } from "@/integrations/supabase/client";
import { handleSupabaseFunctionError } from "@/utils/supabaseFunctionsError";
import type { GeneratePdfRequest, GeneratePdfResponse } from "../types/pdf";

/**
 * Generate PDF report for a session
 *
 * This endpoint:
 * - Fetches entity data for the session
 * - Deducts 150 credits from user's balance
 * - Prepares PDF template and input data
 * - Returns data for client-side PDF generation using @pdfme/generator
 *
 * @param request - Session ID for which to generate the PDF
 * @returns Promise with PDF template and inputs
 * @throws {InsufficientCreditsError} If user doesn't have enough credits (150 required)
 * @throws {Error} For other errors
 *
 * @example
 * try {
 *   const pdfData = await generatePdf({ sessionId: "123" });
 *   await generatePDFReport(pdfData); // Client-side PDF generation
 * } catch (error) {
 *   if (error instanceof InsufficientCreditsError) {
 *     console.log(`Need ${error.shortage} more credits`);
 *   }
 * }
 */
export async function generatePdf(
  request: GeneratePdfRequest
): Promise<GeneratePdfResponse> {
  const { data, error } = await supabase.functions.invoke("api/generate-pdf", {
    method: "POST",
    body: request,
  });

  // Handle errors (including insufficient credits)
  await handleSupabaseFunctionError(error);

  // Validate response format
  if (!data || !data.template || !Array.isArray(data.inputs)) {
    console.error("Invalid generate PDF response:", data);
    throw new Error("Invalid response format from generate-pdf endpoint");
  }

  return data;
}
